import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Observable, BehaviorSubject, throwError } from 'rxjs';
import { map, catchError, tap, shareReplay } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

interface KeycloakTokenResponse {
  access_token: string;
  expires_in: number;
  refresh_expires_in: number;
  token_type: string;
  scope: string;
}

@Injectable({
  providedIn: 'root'
})
export class UcontrolAuthService {
  private tokenSubject = new BehaviorSubject<string | null>(null);
  private tokenExpiryTime: number = 0;
  private tokenRequest$: Observable<string> | null = null;

  constructor(private http: HttpClient) {}

  /**
   * Ottiene il token Keycloak per il Bridge UControl (cached o nuovo)
   */
  getToken(): Observable<string> {
    if (this.tokenSubject.value && this.isTokenValid()) {
      return new Observable<string>((observer) => {
        observer.next(this.tokenSubject.value!);
        observer.complete();
      });
    }

    if (this.tokenRequest$) {
      return this.tokenRequest$;
    }

    this.tokenRequest$ = this.requestNewToken().pipe(
      tap(() => {
        this.tokenRequest$ = null;
      }),
      shareReplay(1)
    );

    return this.tokenRequest$;
  }

  /**
   * Richiede un nuovo token a Keycloak usando client_credentials (client Ucontrol-api-s2s)
   */
  private requestNewToken(): Observable<string> {
    const config = environment.ucontrol.keycloak;

    const body = new URLSearchParams();
    body.set('grant_type', 'client_credentials');
    body.set('client_id', config.clientId);
    body.set('client_secret', config.clientSecret);

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });

    console.log('[UControl Auth] Richiesta token a', config.tokenEndpoint, 'per client', config.clientId);

    return this.http.post<KeycloakTokenResponse>(config.tokenEndpoint, body.toString(), { headers }).pipe(
      map((response) => {
        const token = response.access_token;
        const expiresIn = response.expires_in;

        this.tokenSubject.next(token);
        this.tokenExpiryTime = Date.now() + (expiresIn - 30) * 1000;

        console.log('✅ UControl Bridge token obtained, expires in', expiresIn, 'seconds');
        return token;
      }),
      catchError((error: HttpErrorResponse) => {
        const description = this.describeKeycloakError(error);
        console.error(
          `❌ Failed to obtain UControl Bridge token — HTTP ${error.status} ${error.statusText}: ${description}`,
          '\nFull error:', error,
          '\nResponse body:', error.error
        );
        this.tokenSubject.next(null);
        return throwError(() => new Error(`Keycloak (${config.clientId}): ${description}`));
      })
    );
  }

  /**
   * Estrae un messaggio leggibile dalla risposta di errore di Keycloak
   * (tipicamente { error, error_description } su un 400/401).
   */
  private describeKeycloakError(error: HttpErrorResponse): string {
    const body = error?.error;
    if (body && typeof body === 'object') {
      if (body.error_description) return body.error_description;
      if (body.error) return body.error;
    }
    if (typeof body === 'string' && body.trim()) return body;
    if (error?.status === 0) return 'Nessuna risposta dal server (rete/CORS?)';
    return error?.message || `HTTP ${error?.status}`;
  }

  private isTokenValid(): boolean {
    return Date.now() < this.tokenExpiryTime;
  }

  /**
   * Invalida il token corrente (forza refresh)
   */
  invalidateToken(): void {
    this.tokenSubject.next(null);
    this.tokenExpiryTime = 0;
  }
}
